import { userSegRedirectEntrypoint } from '@jira/redirect-entrypoints__user-seg-redirect/src/userSegRedirectEntrypoint.entrypoint.tsx';
import { LazyIssueApp } from '@jira/issue__lazy-app/src/LazyIssueApp.tsx';
import { MenuIdProjects } from '@jira/navigation-apps__constants/src/MenuIdProjects.tsx';
import { listEntryPoint } from '@jira/spa-apps__business-jsw-list/src/listEntryPoint.entrypoint.tsx';
import { createPageEntryPoint } from '@jira/platform__routing-create-route-entry-point/src/index.tsx';
import { embeddedIssuePreloadLayoutBuilder } from '@jira/platform__layouts-issue-preload-layout/src/index.tsx';
import { getIssueViewResources } from '@jira/router-resources__issue-view/src/getIssueViewResources.tsx';
import { forgeProjectModuleResource } from '@jira/navigation-apps__resources/src/controllers/forge/index.tsx';
import { staticProjectSidebarConnectAddonsNavigationResource } from '@jira/navigation-apps__resources/src/controllers/sidebar/static-project-sidebar-connect-addons/index.tsx';
import { staticProjectSidebarNavigationResource } from '@jira/navigation-apps__resources/src/controllers/sidebar/static-project-sidebar/index.tsx';
import { createEntry } from '@jira/platform__routing-route-entry/src/createEntry.tsx';
import { composeLayouts } from '@jira/platform__layouts-route-layout/src/GlobalComponent.tsx';
import { getNavigationResources } from '@jira/router-resources__navigation/src/getNavigationResources.tsx';
import { projectContextResource } from '@jira/router-resources__project-context/src/projectContextResource.tsx';
import { incidentExperimentTraitResource } from '@jira/router-resources__software-incidents/src/incidentExperimentTraitResource.tsx';
import { softwareListRoute } from '@jira/router-routes__software-list-routes/src/softwareListRoute.tsx';

import { ListSkeleton } from '@jira/platform__skeletons/src/ui/list/ListSkeleton.tsx';
import { AsyncHorizontalNavJira } from '@jira/navigation-apps__horizontal-nav-jira/src/async.tsx';
import { softwareProjectLayout } from '@jira/platform__layouts-software-project-layout/src/index.tsx';
import { jiraHorizontalNavEntrypoint } from '@jira/navigation-apps__horizontal-nav-jira/src/jiraHorizontalNavEntrypoint.entrypoint.tsx';
import { projectThemeResource } from '@jira/router-resources__project-theme/src/projectThemeResource.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { expVal } from '@atlassian/jira-feature-experiments';
import { ProjectIssueNavigatorSkeleton } from '@jira/platform__skeletons/src/ui/issue-navigator/components/project-app.tsx';
import { NIN_NEW_ISSUE_NAVIGATOR } from '@jira/platform__observability-capabilities/src/constants.tsx';
import { getRouteReporting } from '@jira/router-routes__issue-navigator-entries/src/common/getRouteReporting.tsx';
import { getCommonProjectResources } from '@jira/router-routes__issue-navigator-entries/src/common/getCommonProjectResources.tsx';
import { getSsrCircuitBreaker } from '@jira/router-routes__issue-navigator-entries/src/common/getSsrCircuitBreaker.tsx';
import { adjustSsrFetchTimeout } from '@jira/router-routes__issue-navigator-entries/src/common/getSsrFetchTimeout.tsx';
import {
	issueNavigatorPageEntryPoint,
	issueNavigatorPageEntryPointWithoutPreviewPanel,
} from '@jira/router-routes__issue-navigator-entries/src/ui/index.tsx';
import { getListProjectResources } from './common/getListProjectResources.tsx';
import type { Route } from '@atlassian/react-resource-router';

const listPageEntrypoint = createPageEntryPoint({
	main: listEntryPoint,
	horizontalNavigation: jiraHorizontalNavEntrypoint,
	redirect: userSegRedirectEntrypoint,
});

export const softwareListRouteEntry: Route = createEntry(softwareListRoute, () => ({
	group: 'software',
	skeleton: fg('jira_list_nin_merge') ? ProjectIssueNavigatorSkeleton : ListSkeleton,
	entryPoint() {
		if (fg('jira_list_nin_merge')) {
			return fg('nin_glance_panel_technical_changes')
				? issueNavigatorPageEntryPoint
				: issueNavigatorPageEntryPointWithoutPreviewPanel;
		}
		return listPageEntrypoint;
	},
	meta: fg('jira_list_nin_merge')
		? {
				capability: NIN_NEW_ISSUE_NAVIGATOR,
				reporting: getRouteReporting(),
			}
		: undefined,
	bifrost: {
		bifrostGroup: 'issue-navigator',
		manifestName: 'jira-spa-issue-navigator.json',
		circuitBreakerConfig: fg('jira_list_nin_merge') ? getSsrCircuitBreaker() : undefined,
		// Experiment to test the impact of increasing the resource timeout for issue navigator routes with more performance SSR
		fetchTimeout: adjustSsrFetchTimeout(expVal('nin_ssr_resource_timeout', 'timeout', 0)),
	},
	layout: composeLayouts(
		softwareProjectLayout,
		embeddedIssuePreloadLayoutBuilder([{ query: ['selectedIssue'] }]),
	),
	navigation: {
		menuId: MenuIdProjects,
		horizontal: AsyncHorizontalNavJira,
	},
	resources: fg('jira_list_nin_merge')
		? [...getCommonProjectResources(), ...getIssueViewResources()]
		: [
				...getNavigationResources(),
				...getIssueViewResources(),
				// these have been expanded from getNavigationSidebarProjectResource - please examine whether your route requires these resources in nav4 and remove them if needed
				// A guide to check & cleanup: https://hello.atlassian.net/wiki/x/Q8XybwE
				incidentExperimentTraitResource,
				// eslint-disable-next-line jira/router/no-project-context-resource
				projectContextResource,
				forgeProjectModuleResource,
				staticProjectSidebarNavigationResource,
				staticProjectSidebarConnectAddonsNavigationResource,
				// end of resources expanded from getNavigationSidebarProjectResource
				...getListProjectResources(),
				projectThemeResource,
			],
	afterPaint: fg('jira_list_nin_merge') ? undefined : [LazyIssueApp],
	ufoName: fg('jira_list_nin_merge') ? 'software-list-nin' : 'next-gen-list',
	supportsPreviewPanel: true,
	supportsProjectTheme: expVal('jira-list-full-theming-exp', 'isEnabled', false)
		? 'full'
		: 'contoured',
	isHydrateRootEnabled: fg('jira_list_nin_merge'),
	isStreamingEnabled: fg('jfp-magma-ssr-streaming-enable-nin'),
	excludeFromWrmDeferredLoading: fg('jira_list_nin_merge'),
}));
